import TableRowAction from 'components/common/TableRowAction';
import SoftBadge from 'components/common/SoftBadge';
import CustomAdvanceTable from 'components/common/advance-table/CustomAdvanceTable';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import CloseFacilityModal from 'components/common/modals/CloseFacilityModal';
import dayjs from 'dayjs';
import { parseRouteParams } from 'helpers/utils';
import React, { useState } from 'react';
import { Col, Row, Dropdown } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import paths from 'routes/paths';
import {
  permissionsKeyMap,
  userActions
} from 'routes/permissions/permissionKeys';
import { Can } from 'routes/permissions/Can';
import {
  useOrphanages,
  useDeleteOrphanage,
  useCloseFacility,
  useReopenFacility
} from 'hooks/useBeneficiaries';
import useBranchPermission from 'hooks/useBranchPermission';

function OrphanageList() {
  const { isMainBranch } = useBranchPermission();
  const orphanagesTableColumns = [
    {
      accessor: 'id',
      Header: 'ID',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { simple_id, id } = rowData.row.original;
        return (
          <Link
            to={`/orphanage/${id}/details`}
            className="fw-medium ms-2 text-1100 hover-primary"
          >
            ORP{simple_id}
          </Link>
        );
      }
    },
    {
      accessor: 'name',
      Header: 'Centre name',
      cellProps: {
        className: 'py-2'
      }
    },
    {
      accessor: 'orphans',
      Header: 'Number of orphans',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData =>
        rowData.row.original.orphans_aggregate?.aggregate?.count ?? 0
    },
    {
      accessor: 'created_at',
      Header: 'Registered date',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { created_at } = rowData.row.original;
        return <>{dayjs(created_at).format('DD-MM-YYYY')}</>;
      }
    },
    {
      accessor: 'address',
      Header: 'Address',
      cellProps: {
        className: 'py-2'
      }
    },
    {
      accessor: 'country',
      Header: 'Country',
      cellProps: {
        className: 'py-2'
      }
    },
    // HQ views across branches
    ...(isMainBranch
      ? [
          {
            accessor: 'branch',
            Header: 'Branch',
            cellProps: {
              className: 'py-2'
            },
            Cell: rowData => rowData.row.original.branch?.name || '-'
          }
        ]
      : []),
    {
      accessor: 'closed_at',
      Header: 'Status',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { closed_at, closed_reason } = rowData.row.original;
        return closed_at ? (
          <SoftBadge bg="danger" title={closed_reason || undefined}>
            Closed {dayjs(closed_at).format('DD/MM/YYYY')}
          </SoftBadge>
        ) : (
          <SoftBadge bg="success">Open</SoftBadge>
        );
      }
    },
    {
      accessor: 'email',
      Header: 'Email',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { email } = rowData.row.original;
        return <a href={`mailto:${email}`}>{email}</a>;
      }
    },
    {
      accessor: 'phone',
      Header: 'Phone',
      cellProps: {
        className: 'py-2'
      },
      Cell: rowData => {
        const { phone } = rowData.row.original;
        return <a href={`tel:${phone}`}>{phone}</a>;
      }
    },
    {
      accessor: 'id_',
      Header: '',
      cellProps: {
        className: 'py-2 text-end'
      },
      disableSortBy: true,

      Cell: rowData => {
        const { id, closed_at } = rowData.row.original;
        return (
          <TableRowAction
            onCLickEdit={() =>
              navigate(
                parseRouteParams(paths.editOrphanage, {
                  id
                })
              )
            }
            onCLickDelete={() => handleDeleteClick(id)}
            subject={permissionsKeyMap.facilities.children.orphanage}
          >
            <Can
              I={userActions.update}
              a={permissionsKeyMap.facilities.children.orphanage}
            >
              {closed_at ? (
                <Dropdown.Item onClick={() => reopenFacility(id)}>
                  Reopen
                </Dropdown.Item>
              ) : (
                <Dropdown.Item
                  className="text-warning"
                  onClick={() => setCloseTarget(rowData.row.original)}
                >
                  Close
                </Dropdown.Item>
              )}
            </Can>
          </TableRowAction>
        );
      }
    }
  ];

  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [closeTarget, setCloseTarget] = useState(null);

  const { orphanages, isLoading: loading } = useOrphanages();
  const { deleteOrphanageAsync, isDeleting: deleteLoading } =
    useDeleteOrphanage();
  const { closeFacilityAsync, isClosing } = useCloseFacility('orphanage');
  const { reopenFacility } = useReopenFacility('orphanage');

  const onConfirmClose = async reason => {
    try {
      await closeFacilityAsync({ id: closeTarget.id, reason });
      setCloseTarget(null);
    } catch (error) {
      console.error('Error closing orphanage:', error);
    }
  };

  const handleDeleteClick = id => {
    setSelectedId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteModalHide = () => {
    setSelectedId(null);
    setShowDeleteModal(false);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteOrphanageAsync(selectedId);
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting orphanage:', error);
    }
  };

  return (
    <>
      <ConfirmationModal
        show={showDeleteModal}
        onHide={handleDeleteModalHide}
        loading={deleteLoading}
        title="Delete Orphanage"
        handleConfirm={handleConfirmDelete}
        body="Are you sure you want to delete this Orphanage?"
      />
      <CloseFacilityModal
        show={!!closeTarget}
        kind="orphanage"
        facilityName={closeTarget?.name}
        onHide={() => setCloseTarget(null)}
        onConfirm={onConfirmClose}
        isClosing={isClosing}
      />
      <Row>
        <Col md={12} xxl={12} className="h-md-100">
          {orphanages && (
            <CustomAdvanceTable
              data={orphanages}
              columns={orphanagesTableColumns}
              title="Orphanage"
              addButtonLabel="Add Orphanage"
              onClickAddNew={() => {
                navigate(paths.addOrphanage);
              }}
              loading={loading}
              addNew
              addButtonIcon={'plus'}
              subject={permissionsKeyMap.facilities.children.orphanage}
            />
          )}
        </Col>
      </Row>
    </>
  );
}

export default OrphanageList;
